import { Link, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import api from '../api/axios';
import AppShell from '../components/AppShell';
import { downloadInspectionReportPdf } from '../utils/reportPdf';
import { isLocalInspectionId } from '../offline/drafts';

export default function InspectionMenu() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inspection, setInspection] = useState(null);
  const [types, setTypes] = useState([]);
  const [finishing, setFinishing] = useState(false);
  const [error, setError] = useState('');

  const isLocal = isLocalInspectionId(id);

  useEffect(() => {
    Promise.all([
      api.get(`/inspections/${id}`),
      api.get('/inspection-types'),
    ]).then(([inspectionRes, typesRes]) => {
      setInspection(inspectionRes.data);
      const activeTypes = (typesRes.data || []).filter((t) => t.is_active);
      setTypes(activeTypes);
    }).catch((err) => {
      // فحص الجهاز رُفع إلى الخادم: ننتقل إلى نسخته في الخادم.
      const serverId = err?.response?.data?.server_id;
      if (serverId) {
        navigate(`/inspection/${serverId}/menu`, { replace: true });
        return;
      }
      setError(err?.response?.data?.message || err?.userMessage || 'تعذّر تحميل بيانات الفحص.');
    });
  }, [id, navigate]);

  const cards = types.map((t) => ({
    id: t.id,
    key: t.done_field,
    sectionKey: t.key,
    supportsItems: Boolean(t.supports_items),
    title: `${t.icon ? `${t.icon} ` : ''}${t.name}`,
    path: t.route_path,
    supportsRun: Boolean(t.route_path) && (Boolean(t.supports_items) || ['computer', 'body'].includes(t.key) || ['steering', 'engine'].includes(t.route_path)),
  }));

  const resultsBySection = (inspection?.inspection_results || []).reduce((acc, result) => {
    const section = result?.inspection_item?.section;
    if (!section) return acc;
    acc[section] = true;
    return acc;
  }, {});

  const bodyDone = Boolean((inspection?.body_results || []).length);
  const computerDone = Boolean(inspection?.computer_result);
  const resolveDone = (card) => {
    const hasDirectDoneField = Boolean(card.key) && Object.prototype.hasOwnProperty.call(inspection || {}, card.key);

    if (hasDirectDoneField) {
      return Boolean(inspection?.[card.key]);
    }

    if (card.sectionKey === 'body') return bodyDone;
    if (card.sectionKey === 'computer') return computerDone;
    return Boolean(resultsBySection[card.sectionKey]);
  };

  const finishInspection = async () => {
    if (finishing) return;

    const allDone = cards.every((c) => resolveDone(c));
    const finishAction = isLocal ? 'إنهاء الفحص الآن' : 'إنهاء الفحص وتصدير التقرير PDF الآن';
    const confirmMessage = allDone
      ? `هل تريد ${finishAction}؟`
      : `بعض الأقسام غير مكتملة. هل تريد ${finishAction}؟`;

    if (!window.confirm(confirmMessage)) return;

    setError('');
    setFinishing(true);
    try {
      await api.patch(`/inspections/${id}/complete`, {});
      // التقرير يُولَّد في الخادم، فيكون متاحًا بعد رفع فحص الجهاز.
      if (isLocal) {
        window.alert('تم إنهاء الفحص وحفظه على الجهاز. ارفعه بالمزامنة (زر ↻) بعد اختيار «أونلاين»، ويصبح تقريره متاحًا بعدها.');
        navigate('/sync');
        return;
      }
      try {
        await downloadInspectionReportPdf(id);
      } catch (pdfError) {
        window.alert(pdfError?.message || 'تم إنهاء الفحص، لكن تعذر تنزيل ملف PDF تلقائيًا. يمكنك تنزيله من صفحة التقرير.');
      }
      navigate(`/inspection/${id}/report`);
    } catch (err) {
      setError('تعذر إنهاء الفحص الآن. حاول مرة أخرى.');
    } finally {
      setFinishing(false);
    }
  };

  return (
    <AppShell title={`قائمة الفحص #${inspection?.display_number || inspection?.branch_serial || id}`}>
      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {cards.map((c) => {
          const done = resolveDone(c);
          return (
            <div key={c.id} className={`rounded-2xl border p-5 shadow-sm ${done ? 'bg-green-50 border-green-300' : 'bg-white'}`}>
              <h3 className="text-xl font-bold mb-3">{c.title}</h3>
              {c.supportsRun ? (
                <Link to={`/inspection/${id}/${c.path}`} className="inline-block rounded-xl bg-primary px-4 py-2 text-white">
                  ابدأ {done ? '✅' : ''}
                </Link>
              ) : (
                <div className="inline-flex flex-col gap-1">
                  <span className="inline-block rounded-xl bg-slate-200 px-4 py-2 text-slate-600">
                    قريبًا
                  </span>
                  {!c.path && (
                    <span className="text-xs text-slate-500">هذا النوع يحتاج route_path داخل إعدادات أنواع الفحص.</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-6 flex justify-center">
        <button
          onClick={finishInspection}
          disabled={finishing}
          className="rounded-2xl bg-primary px-8 py-3 text-white text-lg font-bold shadow disabled:opacity-60"
        >
          {isLocal
            ? (finishing ? 'جاري إنهاء الفحص...' : '🏁 إنهاء الفحص (يُرفع عند الاتصال)')
            : (finishing ? 'جاري إنهاء الفحص وتجهيز PDF...' : '🏁 إنهاء الفحص وتصدير PDF')}
        </button>
      </div>
    </AppShell>
  );
}
